import React, { useState, useEffect } from "react";
import "./CampaignWizard.scss";
import axios from "axios";
import { API_BASE_URL } from "../../api/config";
import { IoInformationCircleOutline } from "react-icons/io5";
import { IoIosCheckmarkCircle } from "react-icons/io";

interface FollowUpQuestion {
  id: string;
  question: string;
  category: string;
  input_type: "text" | "choice" | "choice_with_text" | "choice__only";
  options?: string[];
  placeholder?: string;
  importance?: string;
  required?: boolean;
  answer?: string;
}

interface CampaignWizardProps {
  initialDescription: string;
  onComplete: (
    enhancedDescription: string,
    answers?: FollowUpQuestion[],
  ) => void;
  onCancel: () => void;
  inline?: boolean;
}

const CampaignWizard: React.FC<CampaignWizardProps> = ({
  initialDescription,
  onComplete,
  onCancel,
  inline = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [questions, setQuestions] = useState<FollowUpQuestion[]>([]);
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completenessScore, setCompletenessScore] = useState(0);
  const [useCustomAnswer, setUseCustomAnswer] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    analyzeDescription();
  }, []);

  const analyzeDescription = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/campaign/analyze-description`,
        {
          description: initialDescription,
        },
      );

      const { questions: followUpQuestions, completeness_score } =
        response.data;

      if (!followUpQuestions || followUpQuestions.length === 0) {
        // Description is complete, proceed directly
        onComplete(initialDescription, []);
      } else {
        setQuestions(followUpQuestions);
        setCompletenessScore(completeness_score);
      }
    } catch (err) {
      setError("Failed to analyze campaign description");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId: string, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNext = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentQuestion = questions[currentStep];

    // Validate required questions
    if (currentQuestion.required && !answers[currentQuestion.id]) {
      setError("This question is required");
      return;
    }

    setError(null);

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // All questions answered, enhance the description
      enhanceDescription();
    }
  };

  const handlePrevious = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleSkip = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentQuestion = questions[currentStep];

    if (currentQuestion.required) {
      setError("This question cannot be skipped");
      return;
    }

    handleNext();
  };

  const enhanceDescription = async () => {
    try {
      setLoading(true);

      // Format answers with questions
      const formattedAnswers = questions.map((q) => ({
        question: q.question,
        answer: answers[q.id] || "Not specified",
        category: q.category,
      }));

      const response = await axios.post(
        `${API_BASE_URL}/api/campaign/enhance-description`,
        {
          original_description: initialDescription,
          answers: formattedAnswers,
        },
      );

      const { enhanced_description } = response.data;
      onComplete(
        enhanced_description,
        questions.map((q) => ({ ...q, answer: answers[q.id] })),
      );
    } catch (err) {
      setError("Failed to enhance campaign description");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="campaign-wizard loading">
        <div className="spinner"></div>
        <p>Analyzing your campaign description...</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div
      className={`campaign-wizard bg-transparent p-0 border-0 ${inline ? "inline" : ""}`}
    >
      <div className="wizard-header mb-3.5">
        <h2 className="font-bold text-xl">Let's Improve Your Campaign</h2>
        <p className="completeness">
          Initial completeness:{" "}
          <span className="score">{completenessScore}%</span>
        </p>
      </div>

      <div className="progress-bar mb-5">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="question-section">
        <div className="flex justify-between items-center mb-3.5">
          <h3 className="question text-[#17150B] text-base font-medium mb-0">
            {currentQuestion.question}
          </h3>
          <div className="question-number text-xs capitalize text-[#7D7B78] font-normal mb-0">
            Question {currentStep + 1} of {questions.length}
          </div>
        </div>

        {currentQuestion.importance && (
          <p className="importance-note items-center bg-[#FFE9C5] text-sm text-[#17150B] rounded-[14px]">
            <IoInformationCircleOutline /> {currentQuestion.importance}
          </p>
        )}

        <div className="answer-section">
          {currentQuestion.input_type === "text" && (
            <>
              <textarea
                className="text-input"
                placeholder={currentQuestion.placeholder}
                value={answers[currentQuestion.id] || ""}
                onChange={(e) =>
                  handleAnswer(currentQuestion.id, e.target.value)
                }
                rows={4}
              />
              {currentQuestion.placeholder &&
                currentQuestion.placeholder.startsWith("e.g.") && (
                  <div className="suggestion-hint">
                    <button
                      type="button"
                      className="suggestion-button"
                      onClick={() => {
                        const exampleText = currentQuestion.placeholder
                          ?.replace("e.g. ", "")
                          .replace("e.g., ", "");
                        if (exampleText) {
                          handleAnswer(currentQuestion.id, exampleText);
                        }
                      }}
                    >
                      💡 Use example:{" "}
                      {currentQuestion.placeholder
                        ?.replace("e.g. ", "")
                        .replace("e.g., ", "")}
                    </button>
                  </div>
                )}
            </>
          )}

          {currentQuestion.input_type === "choice" &&
            currentQuestion.options && (
              <div className="choice-buttons">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    className={`choice-button ${answers[currentQuestion.id] === option ? "selected" : ""}`}
                    onClick={() => handleAnswer(currentQuestion.id, option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}

          {(currentQuestion.input_type === "choice_with_text" || currentQuestion.input_type === "choice__only") &&
            currentQuestion.options && (
              <div className="choice-with-text">
                <div className="quick-options">
                  <p className="options-label">Quick options:</p>
                  <div className="choice-buttons">
                    {currentQuestion.options.map((option, idx) => (
                      <button
                        key={idx}
                        className={`choice-button flex items-center justify-between ${
                          !useCustomAnswer[currentQuestion.id] &&
                          answers[currentQuestion.id] === option
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          setUseCustomAnswer((prev) => ({
                            ...prev,
                            [currentQuestion.id]: false,
                          }));
                          handleAnswer(currentQuestion.id, option);
                        }}
                      >
                        {option}
                        <IoIosCheckmarkCircle />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="custom-option">
                  <p className="or-divider">— or —</p>
                  <button
                    className={`custom-toggle ${useCustomAnswer[currentQuestion.id] ? "active" : ""}`}
                    onClick={() =>
                      setUseCustomAnswer((prev) => ({
                        ...prev,
                        [currentQuestion.id]: true,
                      }))
                    }
                  >
                    Provide custom answer
                  </button>

                  {useCustomAnswer[currentQuestion.id] && (
                    <textarea
                      className="text-input custom-input"
                      placeholder={currentQuestion.placeholder}
                      value={answers[currentQuestion.id] || ""}
                      onChange={(e) =>
                        handleAnswer(currentQuestion.id, e.target.value)
                      }
                      rows={3}
                    />
                  )}
                </div>
              </div>
            )}
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span> {error}
          </div>
        )}
      </div>

      <div className="wizard-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>

        <div className="navigation-buttons">
          {currentStep > 0 && (
            <button
              type="button"
              className="px-4 py-2 bg-white text-gray-600 border border-gray-300 font-medium text-sm rounded-lg hover:bg-gray-50 transition-colors"
              onClick={handlePrevious}
            >
              Previous
            </button>
          )}

          {!currentQuestion.required && (
            <button type="button" className=" px-4 py-2 bg-white text-gray-600 border border-gray-300 font-medium text-sm rounded-lg hover:bg-gray-50 transition-colors" onClick={handleSkip}>
              Skip
            </button>
          )}

          <button type="button" className="btn-primary px-4 py-2 bg-brand-blue text-white font-medium text-sm rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleNext}>
            {currentStep === questions.length - 1 ? "Complete" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignWizard;
